import { useState } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaSpinner, FaImage } from 'react-icons/fa';
import { usePortfolio } from '../../context/PortfolioContext';
import { uploadAPI } from '../../utils/api.js';
import VisibilityToggle from './VisibilityToggle';

const emptyBlog = {
  title: '',
  slug: '',
  excerpt: '',
  cover_image: '',
  published_at: '',
  tags: [],
  read_time: '',
  content: '',
  visible: true
};

const BlogEditor = () => {
  const { portfolioData, addBlog, updateBlog, deleteBlog, loading } = usePortfolio();
  const blogs = portfolioData?.blog || [];
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [editingBlog, setEditingBlog] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newBlog, setNewBlog] = useState(emptyBlog);
  const [toggleLoading, setToggleLoading] = useState({});

  const handleAddBlog = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const blog = {
        ...newBlog,
        slug: newBlog.slug || newBlog.title.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
        published_at: newBlog.published_at || new Date().toISOString(),
        tags: Array.isArray(newBlog.tags) ? newBlog.tags : (newBlog.tags ? newBlog.tags.split(',').map(t => t.trim()) : []),
        visible: newBlog.visible
      };
      const result = await addBlog(blog);
      if (result.success) {
        setNewBlog(emptyBlog);
        setShowAddForm(false);
        setMessage('Blog post added successfully!');
      } else {
        setMessage(result.error || 'Error adding blog post');
      }
    } catch (error) {
      setMessage('Error adding blog post');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateBlog = async () => {
    setIsSaving(true);
    setMessage('');
    try {
      const blog = {
        ...editingBlog,
        tags: Array.isArray(editingBlog.tags) ? editingBlog.tags : (editingBlog.tags ? editingBlog.tags.split(',').map(t => t.trim()) : []),
        visible: editingBlog.visible
      };
      const result = await updateBlog(editingBlog.id, blog);
      if (result.success) {
        setEditingBlog(null);
        setMessage('Blog post updated successfully!');
      } else {
        setMessage(result.error || 'Error updating blog post');
      }
    } catch (error) {
      setMessage('Error updating blog post');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteBlog = async (id) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      const result = await deleteBlog(id);
      if (result.success) {
        setMessage('Blog post deleted successfully!');
      } else {
        setMessage(result.error || 'Error deleting blog post');
      }
    } catch (error) {
      setMessage('Error deleting blog post');
    }
  };

  const handleImageUpload = async (file, blogId = null) => {
    try {
      const response = await uploadAPI.uploadImage(file);
      if (response.success) {
        const imageUrl = response.data.url;
        if (blogId && editingBlog) {
          const updatedBlog = { ...editingBlog, cover_image: imageUrl };
          setEditingBlog(updatedBlog);
          await updateBlog(editingBlog.id, updatedBlog);
        } else {
          setNewBlog({ ...newBlog, cover_image: imageUrl });
        }
        setMessage('Image uploaded successfully!');
      }
    } catch (error) {
      setMessage('Error uploading image');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <FaSpinner className="animate-spin text-4xl text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gray-800 rounded-lg p-6"
    >
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-white">Blog Posts Manager</h3>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setShowAddForm(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
        >
          <FaPlus />
          Add Blog Post
        </motion.button>
      </div>

      {message && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className={`mb-4 p-3 rounded-lg ${
            message.includes('Error') 
              ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
              : 'bg-green-500/20 text-green-300 border border-green-500/30'
          }`}
        >
          {message}
        </motion.div>
      )}

      {/* Add Blog Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-700 rounded-lg p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-medium text-white">Add Blog Post</h4>
            <button
              onClick={() => setShowAddForm(false)}
              className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded"
            >
              <FaTimes />
            </button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleAddBlog(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
              <input type="text" value={newBlog.title} onChange={e => setNewBlog({ ...newBlog, title: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Slug (URL)</label>
              <input type="text" value={newBlog.slug} onChange={e => setNewBlog({ ...newBlog, slug: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" placeholder="auto-generated if left blank" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Excerpt</label>
              <textarea value={newBlog.excerpt} onChange={e => setNewBlog({ ...newBlog, excerpt: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tags (comma separated)</label>
              <input type="text" value={Array.isArray(newBlog.tags) ? newBlog.tags.join(', ') : newBlog.tags} onChange={e => setNewBlog({ ...newBlog, tags: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Read Time</label>
              <input type="text" value={newBlog.read_time} onChange={e => setNewBlog({ ...newBlog, read_time: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" placeholder="e.g. 5 min read" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Published At</label>
              <input type="date" value={newBlog.published_at ? newBlog.published_at.split('T')[0] : ''} onChange={e => setNewBlog({ ...newBlog, published_at: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Cover Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file); }} className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
                {newBlog.cover_image && (
                  <div className="flex items-center gap-2">
                    <img src={newBlog.cover_image} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    <button type="button" onClick={() => setNewBlog(prev => ({ ...prev, cover_image: '' }))} className="p-1 text-red-400 hover:text-red-300"><FaTimes /></button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content (HTML allowed)</label>
              <textarea value={newBlog.content} onChange={e => setNewBlog({ ...newBlog, content: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={8} />
            </div>
            <div className="flex items-center gap-2">
              <VisibilityToggle checked={newBlog.visible ?? true} onChange={e => setNewBlog({ ...newBlog, visible: e.target.checked })} loading={false} />
            </div>
            <div className="flex gap-3 pt-4">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Adding...' : 'Add Blog Post'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setShowAddForm(false)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Edit Blog Form */}
      {editingBlog && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="bg-gray-700 rounded-lg p-6 mb-8"
        >
          <div className="flex justify-between items-center mb-6">
            <h4 className="text-lg font-medium text-white">Edit Blog Post</h4>
            <button onClick={() => setEditingBlog(null)} className="p-2 text-gray-400 hover:text-white hover:bg-gray-600 rounded"><FaTimes /></button>
          </div>
          <form onSubmit={e => { e.preventDefault(); handleUpdateBlog(); }} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Title *</label>
              <input type="text" value={editingBlog.title} onChange={e => setEditingBlog({ ...editingBlog, title: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Slug (URL)</label>
              <input type="text" value={editingBlog.slug} onChange={e => setEditingBlog({ ...editingBlog, slug: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" placeholder="auto-generated if left blank" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Excerpt</label>
              <textarea value={editingBlog.excerpt} onChange={e => setEditingBlog({ ...editingBlog, excerpt: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={2} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Tags (comma separated)</label>
              <input type="text" value={Array.isArray(editingBlog.tags) ? editingBlog.tags.join(', ') : editingBlog.tags} onChange={e => setEditingBlog({ ...editingBlog, tags: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Read Time</label>
              <input type="text" value={editingBlog.read_time} onChange={e => setEditingBlog({ ...editingBlog, read_time: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" placeholder="e.g. 5 min read" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Published At</label>
              <input type="date" value={editingBlog.published_at ? editingBlog.published_at.split('T')[0] : ''} onChange={e => setEditingBlog({ ...editingBlog, published_at: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Cover Image</label>
              <div className="flex items-center gap-4">
                <input type="file" accept="image/*" onChange={e => { const file = e.target.files[0]; if (file) handleImageUpload(file, editingBlog.id); }} className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" />
                {editingBlog.cover_image && (
                  <div className="flex items-center gap-2">
                    <img src={editingBlog.cover_image} alt="Preview" className="w-12 h-12 object-cover rounded" />
                    <button type="button" onClick={() => setEditingBlog(prev => ({ ...prev, cover_image: '' }))} className="p-1 text-red-400 hover:text-red-300"><FaTimes /></button>
                  </div>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Content (HTML allowed)</label>
              <textarea value={editingBlog.content} onChange={e => setEditingBlog({ ...editingBlog, content: e.target.value })} className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-lg text-white focus:outline-none focus:border-primary" rows={8} />
            </div>
            <div className="flex items-center gap-2">
              <VisibilityToggle checked={editingBlog.visible ?? true} onChange={e => setEditingBlog({ ...editingBlog, visible: e.target.checked })} loading={false} />
            </div>
            <div className="flex gap-3 pt-4">
              <motion.button type="submit" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} disabled={isSaving} className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50">
                {isSaving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                {isSaving ? 'Saving...' : 'Save Changes'}
              </motion.button>
              <motion.button type="button" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setEditingBlog(null)} className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors">
                Cancel
              </motion.button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Blog List */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {blogs.map((blog, idx) => (
          <div key={blog.id || idx} className="bg-gray-700 rounded p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="font-semibold text-white flex-1">{blog.title}</h4>
              <button onClick={() => setEditingBlog(blog)} className="text-yellow-400 hover:text-yellow-300"><FaEdit /></button>
              <button onClick={() => handleDeleteBlog(blog.id)} className="text-red-400 hover:text-red-300"><FaTrash /></button>
            </div>
            <div className="text-gray-400 text-xs mb-2">{blog.slug}</div>
            <div className="mb-2">
              {blog.cover_image && <img src={blog.cover_image} alt={blog.title} className="w-full h-32 object-cover rounded mb-2" />}
              <div className="flex flex-wrap gap-2 mb-2">
                {(blog.tags || []).map((tag, i) => (
                  <span key={i} className="bg-primary/10 text-primary px-2 py-1 rounded-full text-xs">{tag}</span>
                ))}
              </div>
              <div className="text-gray-300 text-sm mb-2">{blog.excerpt}</div>
              <div className="text-gray-500 text-xs mb-2">{blog.published_at && (new Date(blog.published_at)).toLocaleDateString()}</div>
              <div className="flex items-center gap-2">
                <VisibilityToggle checked={blog.visible ?? true} onChange={e => { const updatedBlog = { ...blog, visible: e.target.checked }; updateBlog(blog.id, updatedBlog); }} loading={!!toggleLoading[blog.id]} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default BlogEditor; 